import React, {useContext, useState} from "react";
import {
  Avatar, Box, Button, ButtonBase, CircularProgress, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  ArrowDown01Icon, CloudUploadIcon, FloppyDiskIcon, Logout01Icon, PlayIcon, RocketIcon
} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {useAtom} from "jotai";
import {TestChatOpenAtom} from "./atoms";
import {SignInContext} from "../../context/SignInContext";
import {ProjectRoles} from "../../context/AppContext";
import useConfirm from "../../hooks/useConfirm";
import {useRegistryProject} from "../../pages/registry-project/RegistryProjectContext";
import {saveSubProjectVersion} from "../../pages/registry-project/flowVersions";
import {TestChatDrawer} from "./TestChatDrawer";
import {NotificationsMenu} from "./NotificationsMenu";
import {WebrtcConnectButton} from "./WebrtcConnectButton";
import {subProjectLabel} from "../../utils/subProjects";

const TYPE_LABELS = {STAGE: "Stage", PRODUCTION: "Production"};

const ROLE_LABELS = {ADMIN: "Admin", MANAGER: "Manager", AGENT: "Agent", ANNOTATOR: "Annotator"};

const buttonSx = {
  height: 32, px: 1.5, borderRadius: "9px", textTransform: "none", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
  minWidth: 0,
};

const displayName = (user) => [user?.name || user?.firstName, user?.lastname || user?.lastName].filter(Boolean).join(" ")
  || user?.username || "User";

const initialsOf = (user) => displayName(user).split(/\s+/).slice(0, 2).map(part => part[0]).join("").toUpperCase();

// Right side of the registry project header: start, save (stage / production) and the user's avatar.
// role: the user's role in this project. Saving needs manage access (project ADMIN / MANAGER).
export const HeaderActions = ({role, compact = false}) => {
  const {currentUser, onLogout} = useContext(SignInContext);
  const {project, subProject, refreshProject} = useRegistryProject();
  const [chatOpen, setChatOpen] = useAtom(TestChatOpenAtom);
  const [saveAnchor, setSaveAnchor] = useState(null);
  const [userAnchor, setUserAnchor] = useState(null);
  const [saving, setSaving] = useState(null);
  const confirmProduction = useConfirm("Save to production?", "Live calls of this language will run the new version.", "Save");
  const canSave = role === ProjectRoles.ADMIN || role === ProjectRoles.MANAGER;

  const logout = async () => {
    setUserAnchor(null);
    try {
      await onLogout();
    } catch (e) {
      console.error(e);
      toast.error("Failed to log out");
    }
  };

  // Test conversation with the selected language's current (unsaved) flow, chatted with in the drawer
  const start = () => {
    if (!subProject?.flowUid) {
      toast.error("This language has no flow to test");
      return;
    }
    setChatOpen(true);
  };

  // The language's flow becomes a new STAGE / PRODUCTION version (see saveSubProjectVersion)
  const save = async (type) => {
    setSaveAnchor(null);
    if (!subProject?.flowUid) {
      toast.error("This language has no flow to save");
      return;
    }
    if (type === "PRODUCTION" && !(await confirmProduction({
      title: `Save ${subProjectLabel(subProject)} to production?`,
    }))) return;
    setSaving(type);
    try {
      const version = await saveSubProjectVersion({subProject, type, username: currentUser?.username});
      toast.success(`Saved ${version?.name || "a new version"} to ${TYPE_LABELS[type]}`);
      await refreshProject();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || `Failed to save to ${TYPE_LABELS[type]}`);
    } finally {
      setSaving(null);
    }
  };

  const stageVersion = subProject?.stageVersion;
  const productionVersion = subProject?.productionVersion;

  return (
    <Stack direction={"row"} alignItems={"center"} gap={1} sx={{flexShrink: 0}}>
      <WebrtcConnectButton role={role} compact={compact}/>

      <Button
        data-tour="flow-test"
        onClick={start}
        startIcon={<HugeiconsIcon icon={PlayIcon} size={15} strokeWidth={2}/>}
        sx={{
          ...buttonSx, color: "#23264D", border: "1px solid #E4E4EE", bgcolor: "#fff", "&:hover": {bgcolor: "#F4F4FA"},
          ...(compact ? {width: 32, px: 0, "& .MuiButton-startIcon": {m: 0}} : {}),
        }}
      >
        {compact ? null : "Start"}
      </Button>

      {canSave && <Button
        data-tour="flow-save"
        onClick={(e) => setSaveAnchor(e.currentTarget)}
        disabled={Boolean(saving)}
        startIcon={saving ? <CircularProgress size={14} sx={{color: "#fff"}}/> : <HugeiconsIcon icon={FloppyDiskIcon} size={15}/>}
        endIcon={<HugeiconsIcon icon={ArrowDown01Icon} size={14}/>}
        sx={{...buttonSx, display:{xs : "none", md: "inline-flex"}, color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"}, "& .MuiButton-endIcon": {ml: 0.5},
          // keep the label readable while saving (disabled)
          "&.Mui-disabled": {color: "rgba(255, 255, 255, 0.85)", bgcolor: "#4A4F7A"}}}
      >
        {compact ? null : saving ? `Saving ${TYPE_LABELS[saving]}…` : "Save"}
      </Button>}
      <Menu
        anchorEl={saveAnchor}
        open={Boolean(saveAnchor)}
        onClose={() => setSaveAnchor(null)}
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
        transformOrigin={{vertical: "top", horizontal: "right"}}
        PaperProps={{sx: {borderRadius: "12px", mt: 0.75, minWidth: 220, boxShadow: "0 12px 40px rgba(35, 38, 77, 0.16)"}}}
      >
        <MenuItem onClick={() => save("STAGE")} sx={{mx: 0.75, borderRadius: "8px", gap: 1}}>
          <ListItemIcon sx={{minWidth: "28px !important", color: "#6E5BC4"}}><HugeiconsIcon icon={CloudUploadIcon} size={18}/></ListItemIcon>
          <ListItemText primary={"Save Stage"}
                        secondary={stageVersion ? `Now ${stageVersion.name || stageVersion.versionUid}` : "New version for testing"}
                        primaryTypographyProps={{fontSize: 13, fontWeight: 600}} secondaryTypographyProps={{fontSize: 11}}/>
        </MenuItem>
        <MenuItem onClick={() => save("PRODUCTION")} sx={{mx: 0.75, borderRadius: "8px", gap: 1}}>
          <ListItemIcon sx={{minWidth: "28px !important", color: "#1E8A55"}}><HugeiconsIcon icon={RocketIcon} size={18}/></ListItemIcon>
          <ListItemText primary={"Save Production"}
                        secondary={productionVersion ? `Now ${productionVersion.name || productionVersion.versionUid}` : "Publish to live calls"}
                        primaryTypographyProps={{fontSize: 13, fontWeight: 600}} secondaryTypographyProps={{fontSize: 11}}/>
        </MenuItem>
      </Menu>


      <NotificationsMenu project={project}/>

      <ButtonBase onClick={(e) => setUserAnchor(e.currentTarget)} aria-label={"Account"} sx={{ml: 0.5, borderRadius: "50%"}}>
        <Avatar sx={{width: 32, height: 32, fontSize: 12, fontWeight: 700, color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.16)"}}>
          {initialsOf(currentUser)}
        </Avatar>
      </ButtonBase>
      <Menu
        anchorEl={userAnchor}
        open={Boolean(userAnchor)}
        onClose={() => setUserAnchor(null)}
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
        transformOrigin={{vertical: "top", horizontal: "right"}}
        PaperProps={{sx: {borderRadius: "14px", mt: 0.75, minWidth: 240, boxShadow: "0 12px 40px rgba(35, 38, 77, 0.16)"}}}
      >
        <Stack direction={"row"} alignItems={"center"} gap={1.25} sx={{px: 2, py: 1.25}}>
          <Avatar sx={{width: 40, height: 40, fontSize: 14, fontWeight: 700, color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.16)"}}>
            {initialsOf(currentUser)}
          </Avatar>
          <Box sx={{minWidth: 0}}>
            <Typography noWrap sx={{fontSize: 14, fontWeight: 700, color: "#23264D"}}>{displayName(currentUser)}</Typography>
            {currentUser?.username && currentUser.username !== displayName(currentUser) && (
              <Typography noWrap sx={{fontSize: 12, color: "#8A8AA0"}}>{currentUser.username}</Typography>
            )}
          </Box>
        </Stack>
        <Divider sx={{my: 0.5}}/>
        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{px: 2, py: 0.75}}>
          <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>Project role</Typography>
          <Box component={"span"} sx={{
            px: 1, py: 0.25, borderRadius: "999px", fontSize: 12, fontWeight: 600, color: "#4B55C9",
            bgcolor: "rgba(132, 145, 247, 0.12)",
          }}>
            {ROLE_LABELS[role] || role || "—"}
          </Box>
        </Stack>
        <MenuItem onClick={logout} sx={{mx: 0.75, mt: 0.5, borderRadius: "8px", gap: 1, color: "#374151"}}>
          <ListItemIcon sx={{minWidth: "28px !important", color: "#8A8AA0"}}><HugeiconsIcon icon={Logout01Icon} size={18}/></ListItemIcon>
          <ListItemText primary={"Logout"} primaryTypographyProps={{fontSize: 13, fontWeight: 600}}/>
        </MenuItem>
      </Menu>

      <TestChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} project={project} subProject={subProject}/>
    </Stack>
  );
};
